import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";

export interface KPICardData {
  label: string;
  value: string;
  delta?: { value: string; direction: "up" | "down" | "neutral" };
  subtitle?: string;
  sparkline?: number[];
  href?: string;
}

interface KPIRowProps {
  cards: KPICardData[];
}

const DELTA_COLOR_MAP = {
  up: "text-[color:var(--color-success)]",
  down: "text-[color:var(--color-danger)]",
  neutral: "text-[color:var(--color-text-lo)]",
} as const;

const SPARKLINE_STROKE_MAP = {
  up: "var(--color-success)",
  down: "var(--color-danger)",
  neutral: "var(--color-text-lo)",
} as const;

function Sparkline({
  data,
  direction,
}: {
  data: number[];
  direction: "up" | "down" | "neutral";
}) {
  if (data.length === 0) return null;

  const width = 64;
  const height = 24;
  const padding = 2;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data
    .map((value, i) => {
      const x = (i / (data.length - 1)) * (width - padding * 2) + padding;
      const y =
        height - padding - ((value - min) / range) * (height - padding * 2);
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-6 w-16"
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={SPARKLINE_STROKE_MAP[direction]}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function KPICard({ card }: { card: KPICardData }) {
  const content = (
    <Card className="flex h-[120px] flex-col justify-center p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[length:var(--font-size-heading)] font-semibold tabular-nums text-[color:var(--color-text-hi)]">
            {card.value}
          </div>
          <div className="typo-label mt-1 text-[length:var(--font-size-label)] uppercase tracking-[0.08em] text-[color:var(--color-text-md)]">
            {card.label}
          </div>
        </div>
        {card.sparkline && card.delta && (
          <Sparkline data={card.sparkline} direction={card.delta.direction} />
        )}
      </div>
      {card.delta && (
        <div className="mt-2">
          <span
            className={`text-[length:var(--font-size-label)] font-medium tabular-nums ${DELTA_COLOR_MAP[card.delta.direction]}`}
          >
            {card.delta.value}
          </span>
        </div>
      )}
    </Card>
  );

  if (card.href) {
    return (
      <Link
        to={card.href}
        className="rounded-lg transition-shadow hover:ring-1 hover:ring-[color:var(--color-border)]"
      >
        {content}
      </Link>
    );
  }

  return content;
}

export function KPIRow({ cards }: KPIRowProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <KPICard key={card.label} card={card} />
      ))}
    </div>
  );
}
